import { useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  Upload, FileSpreadsheet, FileText, Database, ArrowRight, ArrowLeft,
  CheckCircle2, AlertTriangle, X, Eye, Download, RotateCcw, Loader2
} from "lucide-react";

// ========================
// TYPES
// ========================

type ImportFormat = "csv" | "excel" | "tally";
type ImportDataType = "ledgers" | "customers" | "suppliers" | "inventory" | "opening_balances" | "transactions";

interface ColumnMapping {
  sourceColumn: string;
  targetField: string;
}

interface ParsedRow {
  [key: string]: string | number;
}

interface ImportPreview {
  headers: string[];
  rows: ParsedRow[];
  totalRows: number;
  mappings: ColumnMapping[];
  errors: string[];
  warnings: string[];
}

interface ImportHistoryEntry {
  id: string;
  date: string;
  format: ImportFormat;
  dataType: ImportDataType;
  fileName: string;
  totalRows: number;
  imported: number;
  skipped: number;
  status: "Completed" | "Partial" | "Failed";
}

// ========================
// FIELD TEMPLATES PER DATA TYPE
// ========================

const TARGET_FIELDS: Record<ImportDataType, { field: string; label: string; required: boolean }[]> = {
  ledgers: [
    { field: "name", label: "Ledger Name", required: true },
    { field: "group", label: "Group", required: true },
    { field: "openingBalance", label: "Opening Balance", required: false },
    { field: "openingBalanceType", label: "Dr/Cr", required: false },
    { field: "gstin", label: "GSTIN", required: false },
    { field: "address", label: "Address", required: false },
  ],
  customers: [
    { field: "name", label: "Name", required: true },
    { field: "gstin", label: "GSTIN", required: false },
    { field: "phone", label: "Phone", required: false },
    { field: "email", label: "Email", required: false },
    { field: "address", label: "Address", required: false },
    { field: "city", label: "City", required: false },
    { field: "state", label: "State", required: false },
    { field: "pincode", label: "Pincode", required: false },
    { field: "creditLimit", label: "Credit Limit", required: false },
  ],
  suppliers: [
    { field: "name", label: "Name", required: true },
    { field: "gstin", label: "GSTIN", required: false },
    { field: "phone", label: "Phone", required: false },
    { field: "email", label: "Email", required: false },
    { field: "address", label: "Address", required: false },
    { field: "city", label: "City", required: false },
    { field: "state", label: "State", required: false },
    { field: "creditLimit", label: "Credit Limit", required: false },
  ],
  inventory: [
    { field: "name", label: "Item Name", required: true },
    { field: "group", label: "Stock Group", required: false },
    { field: "unit", label: "Unit", required: false },
    { field: "openingQty", label: "Opening Qty", required: false },
    { field: "openingRate", label: "Opening Rate", required: false },
    { field: "openingValue", label: "Opening Value", required: false },
    { field: "hsnCode", label: "HSN Code", required: false },
    { field: "gstRate", label: "GST Rate %", required: false },
  ],
  opening_balances: [
    { field: "ledgerName", label: "Ledger Name", required: true },
    { field: "amount", label: "Amount", required: true },
    { field: "type", label: "Dr/Cr", required: true },
    { field: "date", label: "Date", required: false },
  ],
  transactions: [
    { field: "date", label: "Date", required: true },
    { field: "voucherType", label: "Voucher Type", required: true },
    { field: "voucherNumber", label: "Voucher No.", required: false },
    { field: "debitLedger", label: "Debit Ledger", required: true },
    { field: "creditLedger", label: "Credit Ledger", required: true },
    { field: "amount", label: "Amount", required: true },
    { field: "narration", label: "Narration", required: false },
  ],
};

const DATA_TYPE_LABELS: Record<ImportDataType, string> = {
  ledgers: "Ledgers",
  customers: "Customers",
  suppliers: "Suppliers",
  inventory: "Inventory Items",
  opening_balances: "Opening Balances",
  transactions: "Transactions",
};

const FORMAT_LABELS: Record<ImportFormat, string> = {
  csv: "CSV File",
  excel: "Excel (XLS/XLSX)",
  tally: "Tally Export (XML)",
};

// ========================
// SEED IMPORT HISTORY
// ========================

const SEED_HISTORY: ImportHistoryEntry[] = [];

// ========================
// PARSERS
// ========================

function parseCSV(text: string): { headers: string[]; rows: ParsedRow[] } {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const rows = lines.slice(1).map((line) => {
    const values = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
    const row: ParsedRow = {};
    headers.forEach((h, i) => { row[h] = values[i] || ""; });
    return row;
  });
  return { headers, rows };
}

function parseExcel(buffer: ArrayBuffer): { headers: string[]; rows: ParsedRow[] } {
  const wb = XLSX.read(buffer, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const data = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: "" });
  if (data.length === 0) return { headers: [], rows: [] };
  const headers = Object.keys(data[0]);
  const rows = data.map((r) => {
    const row: ParsedRow = {};
    headers.forEach((h) => { row[h] = r[h] as string | number; });
    return row;
  });
  return { headers, rows };
}

function parseTallyXML(text: string): { headers: string[]; rows: ParsedRow[] } {
  // Simplified Tally XML parser - handles common Tally export structures
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "text/xml");
  const rows: ParsedRow[] = [];
  
  // Try TALLYMESSAGE > LEDGER
  const ledgers = doc.querySelectorAll("LEDGER");
  if (ledgers.length > 0) {
    ledgers.forEach((el) => {
      const row: ParsedRow = {};
      row["Name"] = el.getAttribute("NAME") || el.querySelector("NAME")?.textContent || "";
      row["Group"] = el.querySelector("PARENT")?.textContent || "";
      row["Opening Balance"] = el.querySelector("OPENINGBALANCE")?.textContent || "0";
      row["Address"] = el.querySelector("ADDRESS")?.textContent || "";
      row["GSTIN"] = el.querySelector("PARTYGSTIN")?.textContent || el.querySelector("GSTIN")?.textContent || "";
      rows.push(row);
    });
    return { headers: ["Name", "Group", "Opening Balance", "Address", "GSTIN"], rows };
  }

  // Try STOCKITEM
  const items = doc.querySelectorAll("STOCKITEM");
  if (items.length > 0) {
    items.forEach((el) => {
      const row: ParsedRow = {};
      row["Name"] = el.getAttribute("NAME") || el.querySelector("NAME")?.textContent || "";
      row["Group"] = el.querySelector("PARENT")?.textContent || "";
      row["Unit"] = el.querySelector("BASEUNITS")?.textContent || "";
      row["Opening Qty"] = el.querySelector("OPENINGBALANCE")?.textContent || "0";
      row["Opening Rate"] = el.querySelector("OPENINGRATE")?.textContent || "0";
      row["HSN Code"] = el.querySelector("HSNCODE")?.textContent || "";
      row["GST Rate"] = el.querySelector("GSTRATE")?.textContent || "";
      rows.push(row);
    });
    return { headers: ["Name", "Group", "Unit", "Opening Qty", "Opening Rate", "HSN Code", "GST Rate"], rows };
  }

  // Try VOUCHER
  const vouchers = doc.querySelectorAll("VOUCHER");
  if (vouchers.length > 0) {
    vouchers.forEach((el) => {
      const row: ParsedRow = {};
      row["Date"] = el.querySelector("DATE")?.textContent || "";
      row["Voucher Type"] = el.getAttribute("VCHTYPE") || el.querySelector("VOUCHERTYPENAME")?.textContent || "";
      row["Voucher No"] = el.querySelector("VOUCHERNUMBER")?.textContent || "";
      row["Narration"] = el.querySelector("NARRATION")?.textContent || "";
      const allEntries = el.querySelectorAll("ALLLEDGERENTRIES\\.LIST, LEDGERENTRIES\\.LIST");
      const debitEntries: string[] = [];
      const creditEntries: string[] = [];
      allEntries.forEach((entry) => {
        const name = entry.querySelector("LEDGERNAME")?.textContent || "";
        const amount = parseFloat(entry.querySelector("AMOUNT")?.textContent || "0");
        if (amount < 0) debitEntries.push(name);
        else creditEntries.push(name);
      });
      row["Debit Ledger"] = debitEntries.join("; ") || "";
      row["Credit Ledger"] = creditEntries.join("; ") || "";
      row["Amount"] = el.querySelector("AMOUNT")?.textContent || "0";
      rows.push(row);
    });
    return { headers: ["Date", "Voucher Type", "Voucher No", "Debit Ledger", "Credit Ledger", "Amount", "Narration"], rows };
  }

  // Fallback: generic element parsing
  const firstChild = doc.documentElement.children[0];
  if (firstChild) {
    const tagName = firstChild.tagName;
    const elements = doc.querySelectorAll(tagName);
    const headerSet = new Set<string>();
    elements.forEach((el) => {
      Array.from(el.children).forEach((c) => headerSet.add(c.tagName));
    });
    const headers = Array.from(headerSet);
    elements.forEach((el) => {
      const row: ParsedRow = {};
      headers.forEach((h) => { row[h] = el.querySelector(h)?.textContent || ""; });
      rows.push(row);
    });
    return { headers, rows };
  }

  return { headers: [], rows: [] };
}

function autoMapColumns(sourceHeaders: string[], dataType: ImportDataType): ColumnMapping[] {
  const targets = TARGET_FIELDS[dataType];
  return targets.map((t) => {
    const match = sourceHeaders.find((h) => {
      const hl = h.toLowerCase().replace(/[_\s.-]/g, "");
      const tl = t.field.toLowerCase();
      const ll = t.label.toLowerCase().replace(/[_\s.-]/g, "");
      return hl === tl || hl === ll || hl.includes(tl) || tl.includes(hl);
    });
    return { sourceColumn: match || "", targetField: t.field };
  });
}

// ========================
// PAGE COMPONENT
// ========================

export default function ImportPage() {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  // Wizard state
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [format, setFormat] = useState<ImportFormat>("csv");
  const [dataType, setDataType] = useState<ImportDataType>("ledgers");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);
  const [history, setHistory] = useState<ImportHistoryEntry[]>(SEED_HISTORY);
  const [mainTab, setMainTab] = useState("import");

  // File handling
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    try {
      let headers: string[] = [];
      let rows: ParsedRow[] = [];

      if (format === "csv") {
        const text = await file.text();
        const result = parseCSV(text);
        headers = result.headers;
        rows = result.rows;
      } else if (format === "excel") {
        const buffer = await file.arrayBuffer();
        const result = parseExcel(buffer);
        headers = result.headers;
        rows = result.rows;
      } else if (format === "tally") {
        const text = await file.text();
        const result = parseTallyXML(text);
        headers = result.headers;
        rows = result.rows;
      }

      if (headers.length === 0) {
        toast({ title: "Parse Error", description: "Could not parse the file. Check the format.", variant: "destructive" });
        return;
      }

      const mappings = autoMapColumns(headers, dataType);
      const errors: string[] = [];
      const warnings: string[] = [];

      // Validate required fields
      const required = TARGET_FIELDS[dataType].filter((f) => f.required);
      for (const req of required) {
        const mapped = mappings.find((m) => m.targetField === req.field);
        if (!mapped?.sourceColumn) {
          warnings.push(`Required field "${req.label}" is not mapped. Please map it manually.`);
        }
      }

      if (rows.length === 0) {
        errors.push("No data rows found in the file.");
      }

      setPreview({ headers, rows, totalRows: rows.length, mappings, errors, warnings });
      setSelectedRows(new Set(rows.map((_, i) => i)));
      setStep(2);
    } catch (err) {
      toast({ title: "File Error", description: "Failed to read the file.", variant: "destructive" });
    }
  }, [format, dataType, toast]);

  const updateMapping = (targetField: string, sourceColumn: string) => {
    if (!preview) return;
    setPreview({
      ...preview,
      mappings: preview.mappings.map((m) => (m.targetField === targetField ? { ...m, sourceColumn } : m)),
    });
  };

  const toggleRow = (idx: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview) return;
    if (selectedRows.size === preview.rows.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(preview.rows.map((_, i) => i)));
  };

  const handleImport = () => {
    if (!preview) return;
    setImporting(true);

    // Simulate import with progress
    setTimeout(() => {
      const imported = selectedRows.size;
      const skipped = preview.rows.length - imported;
      const errors: string[] = [];

      // Add to history
      const entry: ImportHistoryEntry = {
        id: `imp-${crypto.randomUUID().slice(0, 6)}`,
        date: new Date().toISOString().split("T")[0],
        format,
        dataType,
        fileName,
        totalRows: preview.totalRows,
        imported,
        skipped,
        status: skipped === 0 ? "Completed" : "Partial",
      };
      setHistory((prev) => [entry, ...prev]);
      setImportResult({ imported, skipped, errors });
      setImporting(false);
      setStep(4);
    }, 1500);
  };

  const resetWizard = () => {
    setStep(1);
    setPreview(null);
    setFileName("");
    setSelectedRows(new Set());
    setImportResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const getMappedValue = (row: ParsedRow, targetField: string) => {
    if (!preview) return "";
    const mapping = preview.mappings.find((m) => m.targetField === targetField);
    if (!mapping?.sourceColumn) return "";
    return row[mapping.sourceColumn] ?? "";
  };

  const fmt = (n: number) => n.toLocaleString("en-IN");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Data Import</h1>
          <p className="text-sm text-muted-foreground">Import data from Tally, Excel & CSV files</p>
        </div>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="import" className="gap-1.5"><Upload className="h-3.5 w-3.5" /> Import Wizard</TabsTrigger>
          <TabsTrigger value="history" className="gap-1.5"><Database className="h-3.5 w-3.5" /> Import History</TabsTrigger>
        </TabsList>

        <TabsContent value="import">
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mb-6">
            {[
              { n: 1, label: "Select Source" },
              { n: 2, label: "Map Columns" },
              { n: 3, label: "Preview Data" },
              { n: 4, label: "Complete" },
            ].map((s, i) => (
              <div key={s.n} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step >= s.n ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}>
                  {step > s.n ? <CheckCircle2 className="h-4 w-4" /> : s.n}
                </div>
                <span className={`text-sm hidden md:inline ${step >= s.n ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s.label}</span>
                {i < 3 && <ArrowRight className="h-4 w-4 text-muted-foreground mx-1" />}
              </div>
            ))}
          </div>

          {/* Step 1: Select Source */}
          {step === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Import Source</CardTitle>
                  <CardDescription>Choose file format and data type</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>File Format</Label>
                    <Select value={format} onValueChange={(v) => setFormat(v as ImportFormat)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv"><div className="flex items-center gap-2"><FileText className="h-4 w-4" /> CSV File</div></SelectItem>
                        <SelectItem value="excel"><div className="flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Excel (XLS/XLSX)</div></SelectItem>
                        <SelectItem value="tally"><div className="flex items-center gap-2"><Database className="h-4 w-4" /> Tally Export (XML)</div></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data Type</Label>
                    <Select value={dataType} onValueChange={(v) => setDataType(v as ImportDataType)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(DATA_TYPE_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Select File</Label>
                    <Input
                      ref={fileRef}
                      type="file"
                      accept={format === "csv" ? ".csv" : format === "excel" ? ".xls,.xlsx" : ".xml"}
                      onChange={handleFileSelect}
                      className="cursor-pointer"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Expected Fields</CardTitle>
                  <CardDescription>Fields for "{DATA_TYPE_LABELS[dataType]}" import</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {TARGET_FIELDS[dataType].map((f) => (
                      <div key={f.field} className="flex items-center justify-between py-1.5 px-3 rounded bg-muted/50">
                        <span className="text-sm text-foreground">{f.label}</span>
                        <Badge variant={f.required ? "default" : "secondary"} className="text-[10px]">
                          {f.required ? "Required" : "Optional"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick templates */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base">Download Templates</CardTitle>
                  <CardDescription>Use these CSV templates to prepare your data</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(DATA_TYPE_LABELS).map(([k, v]) => (
                      <Button
                        key={k}
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => {
                          const fields = TARGET_FIELDS[k as ImportDataType];
                          const csv = fields.map((f) => f.label).join(",") + "\n";
                          const blob = new Blob([csv], { type: "text/csv" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${k}_template.csv`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        <Download className="h-3.5 w-3.5" /> {v}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {step === 2 && preview && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Column Mapping</CardTitle>
                  <CardDescription>Map source columns to {DATA_TYPE_LABELS[dataType]} fields. {preview.totalRows} rows detected from "{fileName}"</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setStep(1)} className="gap-1.5"><ArrowLeft className="h-3.5 w-3.5" /> Back</Button>
                  <Button size="sm" onClick={() => setStep(3)} className="gap-1.5">Preview <ArrowRight className="h-3.5 w-3.5" /></Button>
                </div>
              </CardHeader>
              <CardContent>
                {preview.warnings.length > 0 && (
                  <div className="mb-4 p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 space-y-1">
                    {preview.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                        <span className="text-foreground">{w}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-3">
                  {TARGET_FIELDS[dataType].map((target) => {
                    const mapping = preview.mappings.find((m) => m.targetField === target.field);
                    return (
                      <div key={target.field} className="grid grid-cols-[200px_40px_1fr] items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{target.label}</span>
                          {target.required && <Badge variant="destructive" className="text-[9px] h-4">Required</Badge>}
                        </div>
                        <ArrowLeft className="h-4 w-4 text-muted-foreground rotate-180" />
                        <Select
                          value={mapping?.sourceColumn || "_none_"}
                          onValueChange={(v) => updateMapping(target.field, v === "_none_" ? "" : v)}
                        >
                          <SelectTrigger className={!mapping?.sourceColumn && target.required ? "border-destructive" : ""}>
                            <SelectValue placeholder="Select column..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none_">— Not mapped —</SelectItem>
                            {preview.headers.map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Preview */}
          {step === 3 && preview && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Import Preview</CardTitle>
                  <CardDescription>{selectedRows.size} of {preview.totalRows} rows selected for import</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setStep(2)} className="gap-1.5"><ArrowLeft className="h-3.5 w-3.5" /> Back</Button>
                  <Button size="sm" onClick={handleImport} disabled={importing || selectedRows.size === 0} className="gap-1.5">
                    {importing ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Importing...</> : <><Upload className="h-3.5 w-3.5" /> Import {selectedRows.size} Rows</>}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {importing && (
                  <div className="mb-4 space-y-2">
                    <Progress value={65} />
                    <p className="text-sm text-muted-foreground text-center">Importing records...</p>
                  </div>
                )}
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox checked={selectedRows.size === preview.rows.length} onCheckedChange={toggleAll} />
                        </TableHead>
                        <TableHead className="w-10">#</TableHead>
                        {TARGET_FIELDS[dataType].map((f) => (
                          <TableHead key={f.field}>{f.label}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.rows.slice(0, 100).map((row, idx) => {
                        const hasError = TARGET_FIELDS[dataType]
                          .filter((f) => f.required)
                          .some((f) => !getMappedValue(row, f.field));
                        return (
                          <TableRow key={idx} className={hasError ? "bg-destructive/5" : ""}>
                            <TableCell>
                              <Checkbox checked={selectedRows.has(idx)} onCheckedChange={() => toggleRow(idx)} />
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                            {TARGET_FIELDS[dataType].map((f) => (
                              <TableCell key={f.field} className="text-sm">
                                {String(getMappedValue(row, f.field)) || (
                                  f.required ? <span className="text-destructive text-xs">Missing</span> : <span className="text-muted-foreground">—</span>
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {preview.rows.length > 100 && (
                    <p className="text-center text-sm text-muted-foreground py-3">Showing first 100 of {fmt(preview.totalRows)} rows</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Complete */}
          {step === 4 && importResult && (
            <Card>
              <CardContent className="pt-8 text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">Import Complete</h2>
                <p className="text-muted-foreground">
                  Successfully imported <span className="font-semibold text-foreground">{fmt(importResult.imported)}</span> {DATA_TYPE_LABELS[dataType].toLowerCase()} from "{fileName}"
                </p>
                <div className="flex justify-center gap-6 py-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-foreground">{fmt(importResult.imported)}</p>
                    <p className="text-xs text-muted-foreground">Imported</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-muted-foreground">{fmt(importResult.skipped)}</p>
                    <p className="text-xs text-muted-foreground">Skipped</p>
                  </div>
                </div>
                {importResult.errors.length > 0 && (
                  <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/5 text-left">
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-sm text-destructive flex items-center gap-2"><X className="h-3.5 w-3.5" />{err}</p>
                    ))}
                  </div>
                )}
                <Button onClick={resetWizard} className="gap-2"><RotateCcw className="h-4 w-4" /> Import More Data</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Import History</CardTitle>
              <CardDescription>Previous data imports</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Data Type</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Imported</TableHead>
                    <TableHead className="text-right">Skipped</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="text-sm">{h.date}</TableCell>
                      <TableCell className="font-medium text-foreground">{h.fileName}</TableCell>
                      <TableCell><Badge variant="outline">{FORMAT_LABELS[h.format]}</Badge></TableCell>
                      <TableCell><Badge variant="secondary">{DATA_TYPE_LABELS[h.dataType]}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{fmt(h.totalRows)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(h.imported)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(h.skipped)}</TableCell>
                      <TableCell>
                        <Badge variant={h.status === "Completed" ? "default" : h.status === "Partial" ? "secondary" : "destructive"}>
                          {h.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
